import type { MessageKey } from './catalogs';
import type { TFunction } from './I18nProvider';

/**
 * Formatage i18n PUR — nombres, décomptes PLURALISÉS, gabarits interpolés et valeur vide.
 * Aucun JSX, aucun accès réseau : testable directement.
 *
 * ORIGINE (audit v2.4.0) — ces helpers sont nés dans `admin/console-format.ts` (lot 7) pour deux
 * défauts visibles à l'écran, puis PROMUS ici au lot 9 : le lot 8 a refusé — à juste titre — de
 * faire dépendre les pages PUBLIQUES du module de la console superadmin. Le formatage d'un nombre
 * et l'accord d'un pluriel ne sont pas une affaire d'administration, ce sont des primitives i18n.
 *
 *  1. **Nombres jamais formatés.** L'app affichait `1234567` là où `formatDateTime` faisait
 *     pourtant correctement son travail juste à côté. Un dépôt réel compte des dizaines de milliers
 *     de fichiers : sans séparateur de milliers, un total devient illisible, et il l'est
 *     DIFFÉREMMENT selon la langue (« 1 234 567 » / « 1,234,567 » / « 1.234.567 »).
 *  2. **Phrases assemblées par concaténation.** La ligne de purge était bâtie en SIX fragments avec
 *     la ponctuation en dur dans le JSX, et ses décomptes toujours au pluriel — elle rendait
 *     « 1 jours », « 1 fichiers ». Une phrase n'est pas une somme de mots : chaque langue en fixe
 *     l'ordre et l'accord, c'est au CATALOGUE de porter le gabarit complet.
 *
 * Le motif de pluriel (`…One` / `…Other` + `{count}`) est celui déjà employé par l'explorateur
 * (`storage.filesCount*`) : on ne réinvente pas une mécanique de pluriel là où le dépôt en a une.
 */

/** Valeur affichée pour une donnée ABSENTE (jamais un `'—'` en dur dispersé dans le JSX). */
export const EMPTY_VALUE = '—';

/**
 * Nombre formaté dans la langue courante (séparateurs de milliers). Repli sur la notation brute
 * si la locale est refusée par la plateforme — un total mal séparé vaut mieux qu'un écran cassé.
 */
export function formatCount(value: number, locale: string): string {
  if (!Number.isFinite(value)) return EMPTY_VALUE;
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

/**
 * Décompte PLURALISÉ générique : `keyOne` au singulier, `keyOther` sinon (avec `{count}` substitué
 * par le nombre FORMATÉ). Le zéro suit le pluriel — c'est la règle du français, de l'allemand et de
 * l'anglais pour ces trois catalogues.
 */
export function pluralCount(
  t: TFunction,
  locale: string,
  count: number,
  keyOne: MessageKey,
  keyOther: MessageKey,
): string {
  if (count === 1) return t(keyOne);
  return t(keyOther).replace('{count}', formatCount(count, locale));
}

/** Substitue une série de jetons `{nom}` dans un gabarit i18n (phrase portée par le catalogue). */
export function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (acc, [key, value]) => acc.split(`{${key}}`).join(value),
    template,
  );
}

/**
 * Libellé accessible « action + cible » — `Sélectionner : photo.jpg`.
 *
 * DÉFAUT CORRIGÉ (audit v2.4.0, lot 9) : ces libellés étaient assemblés dans le JSX par
 * `` `${t('storage.selectFile')} : ${file.name}` ``. L'espace AVANT le deux-points est une règle
 * **typographique française** — fautive en allemand et en anglais — et ce texte est LU PAR LES
 * LECTEURS D'ÉCRAN, donc l'erreur est audible, pas cosmétique. La ponctuation appartient au
 * catalogue : `'{label} : {name}'` en FR, `'{label}: {name}'` en DE/EN.
 */
export function labelFor(t: TFunction, label: string, name: string): string {
  return fill(t('format.labelledItem'), { label, name });
}

/** Variante « libellé — détail » (séparateur em-dash, identique dans les trois langues). */
export function labelWithDetail(t: TFunction, label: string, detail: string): string {
  return fill(t('format.labelledDetail'), { label, detail });
}
